import * as React from 'react'

import { ChevronRightIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import TagIconByTagType from '~/client/src/shared/components/TagIconByTagType/TagIconByTagType'
import FilterCounter from '~/client/src/shared/components/UsersDirectory/components/FilterCounter/FilterCounter'
import TagFilterStore from '~/client/src/shared/components/UsersDirectory/components/TagFilter/TagFilter.store'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationAttributeBase from '~/client/src/shared/models/LocationObjects/LocationAttributeBase'
import { ITag } from '~/client/src/shared/models/Tag'
import { getBandTitleByTagType } from '~/client/src/shared/utils/TagHelper'

interface IProps {
  appliedFilters?: ITag[]
  store: TagFilterStore
  availableTagTypes: TagType[]
  useAlternativeLayout?: boolean
}

const maxTagsCount = 2
const tagIconSize = 18
const categoryTagIconSize = 20

// translated

@observer
export default class TagFilter extends React.Component<IProps> {
  public get store(): TagFilterStore {
    return this.props.store
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    if (this.props.appliedFilters !== prevProps.appliedFilters) {
      this.store.setFilters(this.props.appliedFilters)
    }
  }

  public render() {
    const { selectedCategoryId, clearAll, shouldShowClearAll } = this.store
    const { useAlternativeLayout } = this.props

    const content = selectedCategoryId
      ? this.renderSelectedCategoryContent()
      : this.renderCategories()

    return (
      <div className="tw:flex tw:flex-col tw:flex-1">
        {content}
        {shouldShowClearAll && !useAlternativeLayout && (
          <span
            className="tw:text-title-sm tw:text-primary tw:text-end tw:p-2.5"
            onClick={clearAll}
          >
            {Localization.translator.clearAll_items}
          </span>
        )}
      </div>
    )
  }

  private renderCategories(): JSX.Element[] {
    const { selectCategory, selectedFiltersByTagType } = this.store
    const { useAlternativeLayout } = this.props

    return this.props.availableTagTypes.map(tagType => {
      const selectedFilters = selectedFiltersByTagType[tagType] || []
      const selectedFiltersCount = selectedFilters.length
      const delta = selectedFiltersCount - maxTagsCount

      return (
        <>
          <div className="tw:border-0 tw:border-b tw:border-solid tw:border-border">
            <div
              key={tagType}
              className={classList({
                'tw:flex tw:items-center tw:cursor-pointer tw:[&_.hierarchy-delivery-attribute-tag-content]:max-w-[55px]': true,
                'tw:p-[15px]': !useAlternativeLayout,
                'tw:px-[15px] tw:py-2.5': useAlternativeLayout,
              })}
              onClick={selectCategory.bind(null, tagType)}
            >
              {!useAlternativeLayout && (
                <TagIconByTagType
                  size={categoryTagIconSize}
                  tagType={tagType}
                  className="tw:grow-0 tw:mr-2"
                />
              )}
              <div className="tw:grow-0 tw:mr-3 tw:whitespace-nowrap">
                {getBandTitleByTagType(tagType)}
              </div>

              <div className="tw:flex tw:items-center tw:flex-1">
                {!useAlternativeLayout && this.renderTags(tagType)}
              </div>

              {delta > 0 && !useAlternativeLayout && (
                <FilterCounter count={`+${delta}`} />
              )}
              {useAlternativeLayout && selectedFilters.length > 0 && (
                <FilterCounter count={`${selectedFilters.length}`} />
              )}
              <ChevronRightIcon size={16} className="tw:ml-auto" />
            </div>
            {useAlternativeLayout && selectedFilters.length > 0 && (
              <div className="tw:flex tw:items-center tw:flex-wrap tw:px-[15px] tw:pb-2.5">
                {useAlternativeLayout && this.renderTags(tagType, true)}
              </div>
            )}
          </div>
        </>
      )
    })
  }

  private renderTags(
    tagType: TagType,
    noMaxTags: boolean = false,
  ): React.ReactNode {
    const { toggleTag, selectedFiltersByTagType } = this.store
    const selectedFilters = selectedFiltersByTagType[tagType] || []

    return selectedFilters
      .slice(0, noMaxTags ? selectedFilters.length : maxTagsCount)
      .map(tag => (
        <div
          key={tag.id}
          className="tw:flex tw:items-center tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:mr-2 tw:py-1"
        >
          <SitemapAttributeTag
            isRemovable={true}
            dataObject={tag as LocationAttributeBase}
            shouldShowAsTag={true}
            onRemove={toggleTag.bind(null, tag)}
            contentContainerClassName="tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis"
          >
            <span>{tag.name}</span>
          </SitemapAttributeTag>
        </div>
      ))
  }

  private renderSelectedCategoryContent(): JSX.Element[] {
    const { tagsBySelectedCategories, toggleTag, isFilterSelected } = this.store
    const { useAlternativeLayout } = this.props

    return tagsBySelectedCategories.map(tag => (
      <div
        key={tag.id}
        className={classList({
          'tw:flex tw:items-center tw:border-0 tw:border-b tw:border-solid tw:border-border tw:cursor-pointer': true,
          'tw:p-[15px]': !useAlternativeLayout,
          'tw:px-[15px] tw:py-2.5': useAlternativeLayout,
        })}
        onClick={toggleTag.bind(null, tag)}
      >
        <Checkbox checked={isFilterSelected(tag.id)} />
        <SitemapAttributeTag
          dataObject={tag as LocationAttributeBase}
          shouldShowAsTag={false}
          iconSize={tagIconSize}
          shouldHideIcon={useAlternativeLayout}
        >
          <span>{tag.name}</span>
        </SitemapAttributeTag>
      </div>
    ))
  }
}
